// Common configuration for API
// Simplified version without authentication requirements

import axios, { AxiosInstance } from 'axios';
import { Cookies } from 'react-cookie';
import { API_BASE_URL, DEFAULT_HEADERS, COOKIE_DOMAIN, LOGIN_URL } from '../config/env';

const cookies = new Cookies();

// Helper function to get CSRF token from the backend
const getCsrfToken = async (): Promise<string | undefined> => {
  try {
    const response = await axios.get(`${API_BASE_URL}users/csrf`, {
      withCredentials: true,
      headers: DEFAULT_HEADERS
    });
    
    // Store CSRF token in cookie
    if (response.data.csrf_token) {
      cookies.set('csrftoken', response.data.csrf_token, {
        path: '/',
        secure: true,
        sameSite: 'none',
        domain: COOKIE_DOMAIN
      });
    }
    
    return response.data.csrf_token;
  } catch (error) {
    console.error('Failed to get CSRF token:', error);
    return undefined;
  }
};

// Helper function to create a new axios instance
export const createAxiosInstance = (): AxiosInstance => {
  const instance = axios.create({
    baseURL: API_BASE_URL,  // Use the full API URL from env.ts
    headers: DEFAULT_HEADERS,
    withCredentials: true
  });

  // Add request interceptor to ensure CSRF token is included
  instance.interceptors.request.use(
    async (config) => {
      // Get CSRF token from cookie or fetch it
      let csrfToken = cookies.get('csrftoken');
      if (!csrfToken) {
        csrfToken = await getCsrfToken();
      }

      if (csrfToken) {
        config.headers['X-CSRFToken'] = csrfToken;
      }

      // Ensure cookies are included
      config.withCredentials = true;

      // Ensure headers are properly set
      if (config.headers) {
        Object.assign(config.headers, DEFAULT_HEADERS);
      }

      return config;
    },
    (error) => {
      console.error('Request interceptor error:', error);
      return Promise.reject(error);
    }
  );

  // Add response interceptor to handle authentication redirects
  instance.interceptors.response.use(
    (response) => response,
    (error) => {
      // Only handle 401/403 errors if we're not already on the login page
      if ((error.response?.status === 401 || error.response?.status === 403) && 
          !window.location.pathname.includes('/login')) {
        // Clear any stored tokens and cookies
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        cookies.remove('csrftoken', { domain: COOKIE_DOMAIN });
        cookies.remove('sessionid', { domain: COOKIE_DOMAIN });
        
        // Redirect to login page with return URL
        const returnUrl = encodeURIComponent(window.location.pathname + window.location.search);
        window.location.href = `${LOGIN_URL}?next=${returnUrl}`;
      }
      return Promise.reject(error);
    }
  );

  return instance;
};